import { useState } from "react";
import { useXrayLayoutSettings } from "@/lib/pr27/tacticalXrayLayout";

interface Props {
  visible: boolean;
  goalPct: number;
  keyPassPct: number;
  defenderRiskHigh: boolean;
}

/**
 * PR-27 — Compact tactical X-Ray rendered outside the pitch.
 * Renders below the radar (or as a top band / side panel) according to
 * user/admin layout setting. Click expands a detail panel.
 */
export function CompactTacticalXrayCard({ visible, goalPct, keyPassPct, defenderRiskHigh }: Props) {
  const settings = useXrayLayoutSettings();
  const [expanded, setExpanded] = useState(false);
  if (!visible || settings.layout === "over_field") return null;

  const goal = Math.round(goalPct * 100);
  const key = Math.round(keyPassPct * 100);

  const containerCls =
    settings.layout === "side_panel"
      ? "hidden lg:block sticky top-4 w-full"
      : settings.layout === "top_band"
      ? "mb-2"
      : "mt-2"; // below_radar

  return (
    <div className={containerCls}>
      <div className="rounded-xl border border-border/60 glass p-2.5 animate-fade-in">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[oklch(0.82_0.24_145)]">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[oklch(0.82_0.24_145)]" />
            RAYOS X TÁCTICO
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="grid w-full grid-cols-2 gap-2 text-left"
          aria-expanded={expanded}
        >
          <Stat label="xG" value={`${goal}%`} color="oklch(0.82 0.24 145)" />
          <Stat label="Pase clave" value={`${key}%`} color="oklch(0.7 0.18 230)" />
        </button>
        {expanded && (
          <div className="mt-2 space-y-1.5 rounded-md border border-border/40 bg-background/40 p-2 text-[10px]">
            <Bar label="xG (Goal)" pct={goal} color="oklch(0.82 0.24 145)" />
            <Bar label="Pase clave" pct={key} color="oklch(0.7 0.18 230)" />
            {defenderRiskHigh && (
              <div className="rounded bg-primary/20 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                Riesgo defensivo alto — defensores cerca del balón
              </div>
            )}
            <p className="text-[10px] leading-snug text-muted-foreground">
              xG estima la probabilidad de gol según la posición y la presión.
              Pase clave anticipa la siguiente acción ofensiva.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-base font-extrabold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Bar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-foreground/80">{label}</span>
        <span className="tabular-nums" style={{ color }}>{pct}%</span>
      </div>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-secondary/50">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: "width 300ms ease" }} />
      </div>
    </div>
  );
}